import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Headline5 from '../Typography/Heading/Headline5';
import Body2 from '../Typography/Body/Body2';
import Overline from '../Typography/Overline';
import Labels from '../Card/Labels';
import breakpoints from '../Theme/breakpoints';
import truncateLines from '../TruncateLines';

const NewsListItem = styled.li`
  padding: 24px 0;
  ${(
    { borderTop },
  ) => borderTop && 'border-top: 1px solid var(--grey-light);'}


  @media (min-width: ${breakpoints.lg}px) {
    &:first-of-type {
      ${({ noBorderFirstItem }) => noBorderFirstItem && 'border-top: none;'}
    }
  }

  @media (min-width: ${breakpoints.md}px) {
    padding: 16px 0;

    &:first-of-type {
      padding-top: ${(properties) => properties.firstItemPaddingTop};
    }
  }
`;

NewsListItem.propTypes = {
  borderTop: PropTypes.bool,
  borderBottom: PropTypes.string,
  firstItemPaddingTop: PropTypes.string,
  lastItemPadding: PropTypes.string,
  noBorderFirstItem: PropTypes.bool,
};

NewsListItem.defaultProps = {
  borderTop: true,
  borderBottom: undefined,
  firstItemPaddingTop: undefined,
  lastItemPadding: undefined,
  noBorderFirstItem: false,
};

const NewsListDivider = styled.div`
  border-top: 1px solid var(--grey-light);
`;

const NewsEndOfListDivider = styled.div`
  @media (min-width: ${breakpoints.md}px) {
    border-top: 1px solid var(--grey-light);
  }
`;

const ListItemTextContainer = styled.div`
  &&& {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0;
    height: auto;
  }
`;

const ListItemTitleContainer = styled.div`
  word-wrap: fit-content;
  ${({ restrictWidth }) => restrictWidth && 'max-width: calc(100% - 96px);'}
`;

const StyledHeadLine5 = styled(Headline5)`
  && {
    @media (min-width: ${breakpoints.md}px) {
      font-size: var(--size-h6);
    }
  }
`;

const NewsListItemContent = ({
  title,
  imageRight,
  category,
  tag,
}) => (
  <>
    <ListItemTextContainer>
      <ListItemTitleContainer
        restrictWidth={imageRight}
      >
        {category && <Overline>{category}</Overline>}
        {title && <Headline5 tag={tag}>{title}</Headline5>}
      </ListItemTitleContainer>
      {imageRight}
    </ListItemTextContainer>
  </>
);

NewsListItemContent.propTypes = {
  title: PropTypes.string,
  imageRight: PropTypes.node,
  category: PropTypes.string,
  tag: PropTypes.string,
};

NewsListItemContent.defaultProps = {
  title: undefined,
  imageRight: undefined,
  category: undefined,
  tag: 'span',
};

const CategoryNewsListItemContent = ({
  title,
  tag,
}) => (
  <>
    <ListItemTextContainer
      className="fu-list-item-content"
      tag="div"
    >
      {title && <StyledHeadLine5 tag={tag}>{title}</StyledHeadLine5>}
    </ListItemTextContainer>
  </>
);

CategoryNewsListItemContent.propTypes = {
  title: PropTypes.string,
  tag: PropTypes.string,
};

CategoryNewsListItemContent.defaultProps = {
  title: undefined,
  tag: 'span',
};

const CategoryNewsListItem = styled.li`
  padding: 16px 0;

  @media (min-width: ${breakpoints.md}px) {
    padding: 12px 8px;
  }
`;

const SearchListItemWrapper = styled.li`
  padding: 24px 0;
  border-top: 1px solid var(--grey-light);
  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }

  @media (min-width: ${breakpoints.sm}px) {
    padding: 24px 16px;
  }
`;

const NewsFeedListItemWrapper = styled.li`
  padding: 16px 0;
  border-top: 1px solid var(--grey-light);
  clear: both;
  &:first-of-type {
    border-top: none;
  }
`;

const SearchResultsItemContainer = styled.div`
  width: 100%;
  display: flex;
  position: relative;
  box-sizing: border-box;
  text-align: left;
  align-items: center;
  justify-content: flex-start;
  text-decoration: none;
`;

const NewsListSummary = styled(Body2)`
  && {
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;
  }

  @media(max-width: ${breakpoints.md}px) {
    ${truncateLines};
  }
`;

const SearchResultsItem = ({
  article,
  date,
  category,
  image,
}) => {
  const { title, summary } = article;

  return (
    <SearchResultsItemContainer>
      {image}
      <div>
        <Headline5 tag="h2">
          {title}
        </Headline5>
        <Body2>
          {date}
          {category}
        </Body2>
        <NewsListSummary>
          {summary}
        </NewsListSummary>
      </div>
    </SearchResultsItemContainer>
  );
};

SearchResultsItem.propTypes = {
  article: PropTypes.shape({
    title: PropTypes.string,
    summary: PropTypes.string,
  }).isRequired,
  date: PropTypes.node,
  category: PropTypes.string,
  image: PropTypes.node,
};

SearchResultsItem.defaultProps = {
  date: undefined,
  category: undefined,
  image: undefined,
};

const featuredMarking = css`
  ::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -12px;
    border-left: 3px solid var(--primary-color);
  }
`;

const NewsFeedItemWrapper = styled.div`
  overflow: visible;
  position: relative;
  ${({ featured }) => featured && featuredMarking}

  @media (min-width: ${breakpoints.md}px) {
    min-height: 134px;
  }
`;

const ImageContainer = styled.div`
  width: 201px;
  max-width: 36vw;
  max-height: 24vw;
  float: right;
  padding: 0 0 16px 8px;

  @media (min-width: ${breakpoints.md}px) {
    padding: 0 0 0 8px;
    height: 134px;
  }
`;

const ArticleTitle = styled(Headline5)`
  && {
    flex-grow: 0;
    flex-basis: min-content;
    line-height: 1.5rem;
    padding: 0 0 4px 0;
    max-width: calc(64vw - 40px);

    @media (min-width: ${breakpoints.md}px) {
      max-width: none;
    }
  }
`;

const ArticleTeaser = styled(Body2)`
  && {
    flex-grow: 1;
    font-family: var(--font-article);

    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    -webkit-line-clamp: 3;
    /* autoprefixer: ignore next */
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;

    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ArticleDetails = styled(Body2)`
  display: flex;
  align-items: center;
  font-family: var(--font-article);
  flex-grow: 0;
  flex-basis: 16px;
  padding: 1px 1px 0;
`;

const ArticleCategory = styled(Overline)`
  padding: 0 8px 0 0;
`;

const ArticleDate = styled.span`
  font-family: var(--font-article);
  color: var(--black-disabled);
  font-size: 14px;
`;

const NewsFeedItem = ({
  article,
  date,
  category,
  image,
}) => {
  const { title, summary, featured } = article;

  return (
    <NewsFeedItemWrapper featured={featured}>
      <ImageContainer>
        { image }
      </ImageContainer>
      <ArticleTitle tag="h2">
        {title}
      </ArticleTitle>
      <ArticleTeaser>
        {summary}
      </ArticleTeaser>
      <ArticleDetails>
        <ArticleCategory>{category}</ArticleCategory>
        <ArticleDate>{date}</ArticleDate>
        <ArticleCategory><Labels featured={featured} /></ArticleCategory>
      </ArticleDetails>
    </NewsFeedItemWrapper>
  );
};

NewsFeedItem.propTypes = {
  article: PropTypes.shape({
    title: PropTypes.string,
    summary: PropTypes.string,
    featured: PropTypes.bool,
  }).isRequired,
  date: PropTypes.node,
  category: PropTypes.string,
  image: PropTypes.node.isRequired,
};

NewsFeedItem.defaultProps = {
  date: undefined,
  category: undefined,
};

export {
  NewsListItemContent, NewsListItem, CategoryNewsListItemContent,
  CategoryNewsListItem, NewsListDivider, NewsEndOfListDivider,
  SearchListItemWrapper, SearchResultsItem,
  NewsFeedListItemWrapper, NewsFeedItem,
};
